import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, CheckCircle, XCircle, Sheet, ExternalLink, Settings, Database, FileSpreadsheet } from 'lucide-react';
import { toast } from 'sonner';
import { testGoogleSheetsConnection } from '@/integrations/googleSheetsWebhook';

interface GoogleSheetsConnectorModalProps {
  onConnect: (config: GoogleSheetsConfig) => Promise<any>;
  children: React.ReactNode;
}

interface GoogleSheetsConfig {
  spreadsheetUrl: string;
  sheetName: string;
  syncMode: 'append' | 'replace' | 'update';
  includeMetadata: boolean;
  includeRawData: boolean;
  customColumns: Array<{key: string; label: string; enabled: boolean}>;
  autoCreateSheet: boolean;
  syncFrequency: 'manual' | 'onChange' | 'daily';
}

export function GoogleSheetsConnectorModal({ onConnect, children }: GoogleSheetsConnectorModalProps) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<'success' | 'error' | null>(null);
  const [testMessage, setTestMessage] = useState('');
  
  const [config, setConfig] = useState<GoogleSheetsConfig>({
    spreadsheetUrl: '',
    sheetName: 'Bill Data',
    syncMode: 'append',
    includeMetadata: true,
    includeRawData: false,
    customColumns: [
      { key: 'invoiceNumber', label: 'Invoice Number', enabled: true },
      { key: 'invoiceDate', label: 'Invoice Date', enabled: true },
      { key: 'supplier', label: 'Supplier', enabled: true },
      { key: 'amountDue', label: 'Amount Due', enabled: true },
      { key: 'accountNumber', label: 'Account Number', enabled: true },
    ],
    autoCreateSheet: true,
    syncFrequency: 'manual'
  });

  const handleTestConnection = async () => {
    if (!config.spreadsheetUrl) {
      toast.error('Please enter a Google Sheets URL');
      return;
    }

    setTesting(true);
    setTestResult(null);
    setTestMessage('');

    try {
      // Test if the URL is accessible
      const response = await fetch(config.spreadsheetUrl, { method: 'HEAD' });
      if (response.ok) {
        setTestResult('success');
        setTestMessage('Google Sheets URL is accessible!');
        toast.success('Google Sheets connection test passed');
      } else {
        throw new Error('URL not accessible');
      }
    } catch (error: any) {
      setTestResult('error');
      setTestMessage(error.message || 'Connection failed');
      toast.error('Google Sheets connection test failed');
    } finally {
      setTesting(false);
    }
  };

  const handleConnect = async () => {
    if (!config.spreadsheetUrl) {
      toast.error('Please enter a Google Sheets URL');
      return;
    }

    setLoading(true);
    try {
      const result = await onConnect(config);
      
      if (result?.csvDownloaded) {
        toast.success('CSV file downloaded! Check the instructions for Google Sheets import.', {
          duration: 8000,
          description: 'A CSV file has been downloaded to your computer. Follow the instructions to import it into Google Sheets.'
        });
      } else {
        toast.success('Google Sheets configuration saved successfully');
      }
      
      setOpen(false);
    } catch (error: any) {
      toast.error(error.message || 'Failed to connect to Google Sheets');
    } finally {
      setLoading(false);
    }
  };

  const addCustomColumn = () => {
    setConfig(prev => ({
      ...prev,
      customColumns: [...prev.customColumns, { key: '', label: '', enabled: true }]
    }));
  };

  const updateCustomColumn = (index: number, field: keyof GoogleSheetsConfig['customColumns'][0], value: any) => {
    setConfig(prev => ({
      ...prev,
      customColumns: prev.customColumns.map((col, i) => 
        i === index ? { ...col, [field]: value } : col
      )
    }));
  };

  const removeCustomColumn = (index: number) => {
    setConfig(prev => ({
      ...prev,
      customColumns: prev.customColumns.filter((_, i) => i !== index)
    }));
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {children}
      </DialogTrigger>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sheet className="h-5 w-5 text-green-600" />
            Connect to Google Sheets
          </DialogTitle>
          <p className="text-sm text-muted-foreground mt-2">
            Sync your parsed bill data directly to a Google Sheet
          </p>
        </DialogHeader>
        
        <Tabs defaultValue="basic" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="basic" className="flex items-center gap-2">
              <FileSpreadsheet className="h-4 w-4" />
              Basic Setup
            </TabsTrigger>
            <TabsTrigger value="advanced" className="flex items-center gap-2">
              <Settings className="h-4 w-4" />
              Advanced Settings
            </TabsTrigger>
          </TabsList>

          <TabsContent value="basic" className="space-y-6 mt-6">
            {/* Instructions */}
            <Alert>
              <FileSpreadsheet className="h-4 w-4" />
              <AlertDescription>
                <strong>How it works:</strong> Enter your Google Sheets URL below. When you sync data, 
                a CSV file will be downloaded that you can import into your Google Sheet. 
                This approach works with any Google Sheet without requiring API authentication.
              </AlertDescription>
            </Alert>

            {/* Basic Configuration */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Database className="h-5 w-5" />
                  Google Sheets Configuration
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="spreadsheetUrl">Google Sheets URL *</Label>
                  <Input
                    id="spreadsheetUrl"
                    placeholder="https://docs.google.com/spreadsheets/d/1BxiMVs0XRA5nFMdKvBdBZjgmUUqptlbs74OgvE2upms/edit"
                    value={config.spreadsheetUrl}
                    onChange={(e) => setConfig(prev => ({ ...prev, spreadsheetUrl: e.target.value }))}
                  />
                  <p className="text-xs text-muted-foreground">
                    Paste the public URL of your Google Sheet. Make sure it's set to "Anyone with the link can view"
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="sheetName">Sheet Name</Label>
                    <Input
                      id="sheetName"
                      placeholder="Bill Data"
                      value={config.sheetName}
                      onChange={(e) => setConfig(prev => ({ ...prev, sheetName: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="syncMode">Sync Mode</Label>
                    <Select
                      value={config.syncMode}
                      onValueChange={(value: 'append' | 'replace' | 'update') => 
                        setConfig(prev => ({ ...prev, syncMode: value }))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="append">Append New Rows</SelectItem>
                        <SelectItem value="replace">Replace All Data</SelectItem>
                        <SelectItem value="update">Update Existing</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium">Test Connection</h4>
                    <p className="text-sm text-muted-foreground">
                      Verify your Google Sheets URL is accessible
                    </p>
                  </div>
                  <Button
                    onClick={handleTestConnection}
                    disabled={testing || !config.spreadsheetUrl}
                    variant="outline"
                  >
                    {testing ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <Sheet className="h-4 w-4 mr-2" />
                    )}
                    Test
                  </Button>
                </div>
                
                {testResult && (
                  <div className="flex items-center gap-2">
                    {testResult === 'success' ? (
                      <CheckCircle className="h-4 w-4 text-green-600" />
                    ) : (
                      <XCircle className="h-4 w-4 text-red-600" />
                    )}
                    <span className={`text-sm ${testResult === 'success' ? 'text-green-600' : 'text-red-600'}`}>
                      {testMessage}
                    </span>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="advanced" className="space-y-6 mt-6">
            {/* Advanced Settings */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="h-5 w-5" />
                  Advanced Configuration
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Data Options */}
                <div className="space-y-4">
                  <h4 className="font-medium">Data Options</h4>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <Label htmlFor="includeMetadata">Include Metadata</Label>
                        <p className="text-sm text-muted-foreground">
                          Add export timestamp and source information
                        </p>
                      </div>
                      <Switch
                        id="includeMetadata"
                        checked={config.includeMetadata}
                        onCheckedChange={(checked) => 
                          setConfig(prev => ({ ...prev, includeMetadata: checked }))
                        }
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <Label htmlFor="includeRawData">Include Raw Data</Label>
                        <p className="text-sm text-muted-foreground">
                          Include original parser response as JSON
                        </p>
                      </div>
                      <Switch
                        id="includeRawData"
                        checked={config.includeRawData}
                        onCheckedChange={(checked) => 
                          setConfig(prev => ({ ...prev, includeRawData: checked }))
                        }
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <Label htmlFor="autoCreateSheet">Auto-create Sheet</Label>
                        <p className="text-sm text-muted-foreground">
                          Automatically create the sheet if it doesn't exist
                        </p>
                      </div>
                      <Switch
                        id="autoCreateSheet"
                        checked={config.autoCreateSheet}
                        onCheckedChange={(checked) => 
                          setConfig(prev => ({ ...prev, autoCreateSheet: checked }))
                        }
                      />
                    </div>
                  </div>
                </div>

                {/* Sync Frequency */}
                <div className="space-y-2">
                  <Label htmlFor="syncFrequency">Sync Frequency</Label>
                  <Select
                    value={config.syncFrequency}
                    onValueChange={(value: 'manual' | 'onChange' | 'daily') => 
                      setConfig(prev => ({ ...prev, syncFrequency: value }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="manual">Manual Only</SelectItem>
                      <SelectItem value="onChange">On Data Change</SelectItem>
                      <SelectItem value="daily">Daily Sync</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Custom Columns */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium">Custom Columns</h4>
                    <Button onClick={addCustomColumn} size="sm" variant="outline">
                      Add Column
                    </Button>
                  </div>
                  <div className="space-y-2">
                    {config.customColumns.map((column, index) => (
                      <div key={index} className="flex items-center gap-2 p-3 border rounded-lg">
                        <Switch
                          checked={column.enabled}
                          onCheckedChange={(checked) => 
                            updateCustomColumn(index, 'enabled', checked)
                          }
                        />
                        <Input
                          placeholder="Column Key"
                          value={column.key}
                          onChange={(e) => updateCustomColumn(index, 'key', e.target.value)}
                          className="flex-1"
                        />
                        <Input
                          placeholder="Display Label"
                          value={column.label}
                          onChange={(e) => updateCustomColumn(index, 'label', e.target.value)}
                          className="flex-1"
                        />
                        <Button
                          onClick={() => removeCustomColumn(index)}
                          size="sm"
                          variant="ghost"
                          className="text-red-600 hover:text-red-700"
                        >
                          Remove
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-6 border-t">
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleConnect}
            disabled={loading || !config.spreadsheetUrl}
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : null}
            Save Configuration
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
